<script setup lang="ts">
import { ref, computed, useTemplateRef, watch, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { UiButton, UiText } from '@shopexperts/shared';
import LoginForm, { type LoginAuthResult } from '../leads/LoginForm.vue';
import SuccessCheckmarkIcon from '../../../assets/icons/success-chekmark.svg';
import { useFeatureFlags } from "@/app/composables/useFeatureFlags";

type SubmissionMode = 'loading' | 'login' | 'success' | 'error';

interface Props {
  currentStep: number;
  totalSteps: number;
  mode: SubmissionMode;
  isLoadingAuthCheck?: boolean;
  hasDraftToken?: boolean;
  isAuthenticated: boolean;
  isSubmitting?: boolean;
  isSubmitted?: boolean;
  isNewAccount?: boolean;
  hasError?: boolean;
  isPreparingSubmission?: boolean;
  /** Whether this step is currently active/visible */
  isActive?: boolean;
  /** Whether auth check data has been fetched at least once */
  hasAuthCheckData?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isLoadingAuthCheck: false,
  hasDraftToken: false,
  isSubmitting: false,
  isSubmitted: false,
  isNewAccount: false,
  hasError: false,
  isPreparingSubmission: false,
  isActive: false,
  hasAuthCheckData: false,
});

const emit = defineEmits<{
  (e: 'authenticated', data: LoginAuthResult): void;
  (e: 'back'): void;
  (e: 'retry'): void;
}>();

const router = useRouter();
const { isEnabled } = useFeatureFlags();
const loginFormRef = useTemplateRef<InstanceType<typeof LoginForm>>('loginFormRef');

// Countdown timer for authenticated users
const REDIRECT_COUNTDOWN_SECONDS = 5;
const countdownSeconds = ref(REDIRECT_COUNTDOWN_SECONDS);
let countdownInterval: ReturnType<typeof setInterval> | null = null;

const showLogin = computed(() => props.mode === 'login');
const showSuccess = computed(() => props.mode === 'success' && props.isSubmitted);
const showError = computed(() => props.mode === 'error' || props.hasError);

// Case 1: Authenticated user (show dashboard redirect with countdown)
const isAuthenticatedSuccess = computed(() => {
  return props.isAuthenticated;
});

// Case 2: New account (not authenticated, show verify email message)
const isNewAccountSuccess = computed(() => {
  return !props.isAuthenticated;
});

// Show loading state:
// 1. While waiting for auth check to complete (only when active)
// 2. While submitting
const showLoading = computed(() => {
  // Only show loading when this step is active
  if (!props.isActive) return false;

  if (props.mode === 'loading') return true;
  if (props.isPreparingSubmission) return true;
  if (props.hasDraftToken && !props.hasAuthCheckData && !props.hasError && !props.isSubmitted) return true;
  if (props.isLoadingAuthCheck && !props.hasError && !props.isSubmitted) return true;
  return props.isSubmitting && !props.isSubmitted && !props.hasError;
});

function onRetry() {
  emit('retry');
}

const isLoading = computed(() => {
  return props.isSubmitting || loginFormRef.value?.isLoading;
});

function onLoginSubmit() {
  loginFormRef.value?.triggerSubmit();
}

function onLoginAuthenticated(data: LoginAuthResult) {
  emit('authenticated', data);
}

function onBack() {
  emit('back');
}

function navigateToDashboard() {
  router.push(isEnabled('enable_v2_projects') ? '/client/projects' : '/client/leads');
}

function navigateToLogin() {
  router.push('/login');
}

// Start countdown when authenticated user sees success
function startCountdown() {
  if (countdownInterval) return;
  countdownSeconds.value = REDIRECT_COUNTDOWN_SECONDS;
  countdownInterval = setInterval(() => {
    countdownSeconds.value--;
    if (countdownSeconds.value <= 0) {
      stopCountdown();
      navigateToDashboard();
    }
  }, 1000);
}

function stopCountdown() {
  if (countdownInterval) {
    clearInterval(countdownInterval);
    countdownInterval = null;
  }
}

// Cleanup on unmount
onUnmounted(() => {
  stopCountdown();
});

// Start countdown when authenticated user submission is complete
watch([() => props.isSubmitted, isAuthenticatedSuccess], ([submitted, isAuth]) => {
  if (submitted && isAuth) {
    startCountdown();
  } else {
    stopCountdown();
  }
}, { immediate: true });

</script>

<template>
  <div class="flex flex-col gap-6">
    <div v-if="showLoading" class="flex flex-col items-center justify-center gap-6 py-12 text-center min-h-[500px]">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-se-primary-600"></div>
      <UiText as="p" variant="p" class="text-se-gray-600">
        Preparing your project submission...
      </UiText>
    </div>

    <div v-else-if="showError" class="flex flex-col items-center justify-center gap-6 py-12 text-center min-h-[500px]">
      <div class="w-20 h-20 flex items-center justify-center rounded-full bg-se-red-100">
        <svg class="w-10 h-10 text-se-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
      </div>

      <div class="flex flex-col gap-2 max-w-xl">
        <UiText as="h3" variant="h3" class="font-semibold text-se-gray-900">
          We couldn't prepare your request
        </UiText>
        
        <UiText as="p" variant="p" class="text-se-gray-600">
          Your form details are still here. Try again, or go back and keep editing without losing your progress.
        </UiText>
      </div>

      <div class="flex gap-3">
        <UiButton
          variant="outline"
          label="Go Back"
          @click="onBack"
        />
        <UiButton
          variant="primary"
          label="Try Again"
          @click="onRetry"
        />
      </div>
    </div>

    <!-- Show success after submission -->
    <div v-else-if="showSuccess" class="flex flex-col items-center justify-center gap-6 py-12 text-center min-h-[500px]">
        <!-- Success Icon -->
        <div class="w-20 h-20">
          <SuccessCheckmarkIcon class="w-full h-full" />
        </div>

        <!-- Success Message - Case 1: Authenticated user -->
        <template v-if="isAuthenticatedSuccess">
          <div class="flex flex-col gap-2 max-w-xl">
            <UiText as="h3" variant="h3" class="font-semibold text-se-gray-900">
              Your project request has been submitted!
            </UiText>
            
            <UiText as="p" variant="p" class="text-se-gray-600">
              We're now matching you with the most suitable expert for your project.
              You can track progress and review proposals in your dashboard.
            </UiText>
          </div>

          <div class="flex flex-col items-center gap-3 w-full max-w-xs">
            <UiButton 
              variant="primary" 
              label="Go to Dashboard"
              @click="navigateToDashboard"
              class="w-full"
            />
            <UiText as="p" variant="p-small" class="text-se-gray-500">
              Redirecting to dashboard in <span class="font-semibold">{{ countdownSeconds }} seconds</span> ...
            </UiText>
          </div>
        </template>

        <!-- Success Message - Case 2: New account (not authenticated) -->
        <template v-else-if="isNewAccountSuccess">
          <div class="flex flex-col gap-2 max-w-xl">
            <UiText as="h3" variant="h3" class="font-semibold text-se-gray-900">
              Your project request has been submitted!
            </UiText>
            
            <UiText as="p" variant="p" class="text-se-gray-600">
              We've sent you an email to <span class="font-semibold">verify your account</span>. Once verified, you'll be 
              able to track your project and review proposals in your dashboard.
              Please check your <span class="font-semibold">spam or junk folder</span> if you don't see it.
            </UiText>
          </div>

          <div class="flex flex-col gap-3 w-full max-w-xs">
            <UiButton 
              variant="primary" 
              label="Dashboard Login"
              @click="navigateToLogin"
              class="w-full"
            />
          </div>
        </template>
    </div>

    <!-- Login View - Case 3: Existing user not logged in -->
    <template v-else-if="showLogin">
      <div class="flex flex-col gap-3">
        <UiText as="h2" variant="h2" class="text-se-gray-900">
          Log in to your account
        </UiText>
        
        <UiText as="p" variant="p" class="text-se-gray-600">
          Access your dashboard to review expert proposals and track progress with your projects.
        </UiText>
      </div>

      <LoginForm
        ref="loginFormRef"
        @authenticated="onLoginAuthenticated"
      />

      <!-- Shared step actions -->
      <div class="flex items-center gap-4 pt-6">
        <UiButton
          type="button"
          variant="primary"
          label="Dashboard Login"
          class="flex-1"
          :disabled="isLoading"
          @click="onLoginSubmit"
        />
      </div>
    </template>
  </div>
</template>
